import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { supabase } from '../../../lib/supabase';

const OrderFilters = ({ filters, onFiltersChange, onClearFilters }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [dealershipOptions, setDealershipOptions] = useState([]);
  const [technicianOptions, setTechnicianOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch real data from database
  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        setLoading(true);
        
        // Fetch dealerships
        const { data: dealerships, error: dealershipError } = await supabase
          .from('car_houses')
          .select('id, name, code')
          .eq('status', 'active')
          .order('name');
        
        
        if (dealershipError) {
          console.error('Error fetching dealerships:', dealershipError);
        } else {
          const dealershipOpts = [
            { value: '', label: 'Alle Autohäuser' },
            ...(dealerships || []).map(d => ({
              value: d.id,
              label: d.name
            }))
          ];
          setDealershipOptions(dealershipOpts);
        }

        // Fetch technicians
        const { data: technicians, error: technicianError } = await supabase
          .from('technicians')
          .select('id, first_name, last_name, email')
          .eq('status', 'active')
          .order('first_name');
        
        
        if (technicianError) {
          console.error('Error fetching technicians:', technicianError);
        } else {
          const technicianOpts = [
            { value: '', label: 'Alle Techniker' },
            { value: 'unassigned', label: 'Nicht zugewiesen' },
            ...(technicians || []).map(t => ({
              value: t.id,
              label: `${t.first_name || ''} ${t.last_name || ''}`.trim() || 'Unbekannter Techniker'
            }))
          ];
          setTechnicianOptions(technicianOpts);
        }
      } catch (error) {
        console.error('Error fetching filter data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFilterData();
  }, []);

  const statusOptions = [
    { value: '', label: 'Alle Status' },
    { value: 'pending', label: 'Neu' },
    { value: 'review', label: 'In Prüfung' },
    { value: 'assigned', label: 'Zugewiesen' },
    { value: 'in_progress', label: 'In Bearbeitung' },
    { value: 'completed', label: 'Erledigt' },
    { value: 'cancelled', label: 'Storniert' },
    { value: 'on_hold', label: 'Pausiert' }
  ];

  const priorityOptions = [
    { value: '', label: 'Alle Prioritäten' },
    { value: 'low', label: 'Niedrig' },
    { value: 'normal', label: 'Normal' },
    { value: 'high', label: 'Hoch' },
    { value: 'urgent', label: 'Dringend' }
  ];

  const handleFilterChange = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const hasActiveFilters = Object.values(filters)?.some(value => value && value !== '');

  // Show loading state while fetching data
  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-4 mb-6">
        <div className="flex items-center justify-center py-8">
          <Icon name="Loader2" size={24} className="animate-spin text-text-secondary mr-2" />
          <span className="text-text-secondary">Lade Filter...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-4 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Icon name="Filter" size={20} className="text-text-secondary" />
          <h3 className="font-medium text-text-primary">Filter</h3>
          {hasActiveFilters && (
            <span className="bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full">
              Aktiv
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              className="text-text-secondary"
            >
              <Icon name="X" size={16} className="mr-1" />
              Zurücksetzen
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="lg:hidden"
          >
            <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={16} />
          </Button>
        </div>
      </div>
      <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 ${!isExpanded ? 'hidden lg:grid' : ''}`}>
        <div>
          <Input
            label="VIN / Kennzeichen"
            type="text"
            placeholder="Suche nach VIN oder Kennzeichen"
            value={filters?.search || ''}
            onChange={(e) => handleFilterChange('search', e?.target?.value)}
          />
        </div>

        <div>
          <Select
            label="Status"
            options={statusOptions}
            value={filters?.status || ''}
            onChange={(value) => handleFilterChange('status', value)}
          />
        </div>

        <div>
          <Select
            label="Autohaus"
            options={dealershipOptions}
            value={filters?.dealership || ''}
            onChange={(value) => handleFilterChange('dealership', value)}
            searchable
          />
        </div>

        <div>
          <Select
            label="Techniker"
            options={technicianOptions}
            value={filters?.technician || ''}
            onChange={(value) => handleFilterChange('technician', value)}
          />
        </div>

        <div>
          <Select
            label="Priorität"
            options={priorityOptions}
            value={filters?.priority || ''}
            onChange={(value) => handleFilterChange('priority', value)}
          />
        </div>

        <div>
          <Input
            label="Von Datum"
            type="date"
            value={filters?.dateFrom || ''}
            onChange={(e) => handleFilterChange('dateFrom', e?.target?.value)}
          />
        </div>

        <div>
          <Input
            label="Bis Datum"
            type="date"
            value={filters?.dateTo || ''}
            onChange={(e) => handleFilterChange('dateTo', e?.target?.value)}
          />
        </div>

        <div className="flex items-end">
          <Button
            variant="outline"
            onClick={() => setIsExpanded(false)}
            className="w-full lg:hidden"
          >
            Filter anwenden
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OrderFilters;
